import { isNil } from 'lodash-es'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { FormPreview } from '@/entities/contract'
import { TaxReceiptType } from '@/entities/user'
import { formatPhoneNumber, useScrollState } from '@/shared/lib'

import { ReceiptForm } from './receipt-form'
import { formatTaxInvoiceNumber } from '../lib/format'
import { TReceiptInformationForm } from '../model/form'
import { useTaxReceiptTypeMap } from '../model/tax-receipt-type'

type Props = {
  defaultFormOpen: boolean
  hasDefaultReceiptInformation: boolean
  onFormOpen?: () => void
  scrollBlock?: ScrollLogicalPosition
}

export const ReceiptInformationUpdate = ({
  defaultFormOpen = false,
  hasDefaultReceiptInformation = false,
  onFormOpen,
  scrollBlock = 'end',
}: Props) => {
  const t = useTranslations('receipt_information')

  const [isFormOpen, setIsFormOpen] = useState(defaultFormOpen)

  const { scrollIntoView } = useScrollState<HTMLDivElement>()

  const { control } = useFormContext<TReceiptInformationForm>()

  const { TAX_RECEIPT_TYPE_MAP } = useTaxReceiptTypeMap()

  const [
    receiptRequestedValue,
    taxReceiptType,
    personalIssuanceNumber,
    businessIssuanceNumber,
    corpId,
  ] = useWatch({
    control,
    name: [
      'receiptRequested',
      'taxReceiptType',
      'personalIssuanceNumber',
      'businessIssuanceNumber',
      'corpId',
    ],
  })

  const getDisplayReceiptInformation = useMemo((): string | null => {
    if (isNil(receiptRequestedValue)) {
      return null
    }

    const receiptRequested = receiptRequestedValue === 'true'

    if (!receiptRequested) {
      return t('status.not_requested.label')
    }

    if (taxReceiptType === TaxReceiptType.CASH_RECEIPT_PERSONAL) {
      return t('status.requested', {
        type: TAX_RECEIPT_TYPE_MAP[taxReceiptType as TaxReceiptType],
        number: formatPhoneNumber(personalIssuanceNumber as string),
      })
    }

    if (taxReceiptType === TaxReceiptType.CASH_RECEIPT_BUSINESS) {
      return t('status.requested', {
        type: TAX_RECEIPT_TYPE_MAP[taxReceiptType as TaxReceiptType],
        number: formatPhoneNumber(businessIssuanceNumber as string),
      })
    }

    if (taxReceiptType === TaxReceiptType.TAX_INVOICE) {
      return t('status.requested', {
        type: TAX_RECEIPT_TYPE_MAP[taxReceiptType as TaxReceiptType],
        number: formatTaxInvoiceNumber(corpId as string),
      })
    }

    return ''
  }, [
    businessIssuanceNumber,
    corpId,
    personalIssuanceNumber,
    receiptRequestedValue,
    taxReceiptType,
    TAX_RECEIPT_TYPE_MAP,
    t,
  ])

  const handleEditButtonClick = () => {
    setIsFormOpen(true)
    onFormOpen?.()

    setTimeout(() => {
      scrollIntoView({ block: scrollBlock, behavior: 'smooth' })
    }, 0)
  }

  useEffect(() => {
    if (!hasDefaultReceiptInformation) {
      setIsFormOpen(defaultFormOpen)

      if (defaultFormOpen) {
        onFormOpen?.()
      }
    }
  }, [hasDefaultReceiptInformation, defaultFormOpen])

  if (!isFormOpen) {
    return (
      <FormPreview
        name="영수증 정보"
        value={getDisplayReceiptInformation}
        onEditButtonClick={handleEditButtonClick}
      />
    )
  }

  return <ReceiptForm scrollBlock={scrollBlock} />
}
